import { Vector2 } from 'three';
import { EventRegister } from '../../Utils';
import { PointerState } from './PointerState';
/**
 * Implements application specific gesture controls as an abstraction over
 * HTML keyboard, touch, and mouse events
 *
 */
export class GestureControl extends EventRegister {
    constructor(canvas) {
        super();
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pointerState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.canvas = canvas;
        this.pointerState = new PointerState(canvas);
        // Ignore context menu (typically right click)
        this.canvas.addEventListener('contextmenu', (ev) => {
            ev.preventDefault();
        });
        // Focus on the canvas by default to register keyboard input
        this.canvas.setAttribute('tabindex', '0');
        // Register all gesture handlers
        this.registerKeyTap();
        this.registerKeyRelease();
        this.registerTap();
        this.registerRelease();
        this.registerSwipe();
        this.registerHover();
        this.registerZoom();
    }
    /**
     * Handle key press gestures.
     */
    registerKeyTap() {
        const handler = (ev) => {
            const callbacks = this.getCallbacks('keytap');
            for (const callback of callbacks) {
                callback(ev);
            }
        };
        this.canvas.addEventListener('keydown', handler);
    }
    /**
     * Handle key release gestures.
     */
    registerKeyRelease() {
        const handler = (ev) => {
            const callbacks = this.getCallbacks('keyrelease');
            for (const callback of callbacks) {
                callback(ev);
            }
        };
        this.canvas.addEventListener('keyup', handler);
    }
    /**
     * Handle tap gestures.
     */
    registerTap() {
        const mouseHandler = () => {
            const active = this.pointerState.getPointers();
            const callbacks = this.getCallbacks('tap');
            if (active.length > 0) {
                for (const callback of callbacks) {
                    callback({
                        position: active[0].current,
                        prevPosition: active[0].previous,
                        ctrlKey: this.pointerState.getCtrlKey(),
                        button: this.pointerState.getButton(),
                    });
                }
            }
        };
        const touchHandler = () => {
            const active = this.pointerState.getPointers();
            const callbacks = this.getCallbacks('tap');
            if (active.length > 0) {
                // Average out the positions of all pointers
                const position = new Vector2();
                const prevPosition = new Vector2();
                for (const pointer of active) {
                    position.add(pointer.current);
                    prevPosition.add(pointer.previous);
                }
                position.divideScalar(active.length);
                prevPosition.divideScalar(active.length);
                for (const callback of callbacks) {
                    callback({
                        position,
                        prevPosition,
                        ctrlKey: this.pointerState.getCtrlKey(),
                        button: active.length === 2 ? 2 : 0,
                    });
                }
            }
        };
        this.canvas.addEventListener('mousedown', mouseHandler);
        this.canvas.addEventListener('touchstart', touchHandler);
    }
    /**
     * Handle release gestures.
     */
    registerRelease() {
        const handler = () => {
            const callbacks = this.getCallbacks('release');
            for (const callback of callbacks) {
                callback({
                    position: this.pointerState.getLastRemoved(),
                    prevPosition: this.pointerState.getLastRemoved(),
                    ctrlKey: this.pointerState.getCtrlKey(),
                    button: this.pointerState.getButton(),
                });
            }
        };
        this.canvas.addEventListener('mouseup', handler);
        this.canvas.addEventListener('touchend', handler);
    }
    /**
     * Handle swipe gestures.
     */
    registerSwipe() {
        const mouseHandler = () => {
            const active = this.pointerState.getPointers();
            const callbacks = this.getCallbacks('swipe');
            if (active.length > 0) {
                for (const callback of callbacks) {
                    callback({
                        position: active[0].current,
                        prevPosition: active[0].previous,
                        ctrlKey: this.pointerState.getCtrlKey(),
                        button: this.pointerState.getButton(),
                    });
                }
            }
        };
        const touchHandler = () => {
            const active = this.pointerState.getPointers();
            const callbacks = this.getCallbacks('swipe');
            if (active.length > 0) {
                // Average out the positions of all pointers
                const position = new Vector2();
                const prevPosition = new Vector2();
                for (const pointer of active) {
                    position.add(pointer.current);
                    prevPosition.add(pointer.previous);
                }
                position.divideScalar(active.length);
                prevPosition.divideScalar(active.length);
                let dot = 1;
                let delta = 1;
                if (active.length >= 2) {
                    const d0 = active[0].current.clone().sub(active[0].previous);
                    const d1 = active[1].current.clone().sub(active[1].previous);
                    delta = (d0.length() + d1.length()) / 2;
                    dot = d1.normalize().dot(d0.normalize());
                }
                // Do not invoke if pointers are moving in separate directions
                // to avoid conflicting with the zoom gesture
                if (dot >= 0.05 && delta >= 0.05) {
                    for (const callback of callbacks) {
                        callback({
                            position,
                            prevPosition,
                            ctrlKey: this.pointerState.getCtrlKey(),
                            button: active.length === 2 ? 2 : 0,
                        });
                    }
                }
            }
        };
        this.canvas.addEventListener('mousemove', mouseHandler);
        this.canvas.addEventListener('touchmove', touchHandler);
    }
    /**
     * Handle hover gestures.
     */
    registerHover() {
        const handler = () => {
            const callbacks = this.getCallbacks('hover');
            for (const callback of callbacks) {
                callback({
                    position: this.pointerState.getHoverPosition(),
                    prevPosition: this.pointerState.getPreviousHoverPosition(),
                });
            }
        };
        this.canvas.addEventListener('mousemove', handler);
        this.canvas.addEventListener('touchmove', handler);
    }
    /**
     * Handle zoom gestures.
     */
    registerZoom() {
        const wheelHandler = (ev) => {
            ev.preventDefault();
            this.canvas.focus();
            const { target, x, y, deltaX, deltaY } = ev;
            const targetElement = target;
            const { left, top } = targetElement.getBoundingClientRect();
            const callbacks = this.getCallbacks('zoom');
            for (const callback of callbacks) {
                callback({
                    position: new Vector2(x - left, y - top),
                    direction: new Vector2(deltaX, deltaY),
                });
            }
        };
        const touchHandler = () => {
            const active = this.pointerState.getPointers();
            const callbacks = this.getCallbacks('zoom');
            if (active.length >= 2) {
                // Only zoom if the two touch points are not moving in the same direction
                const d0 = active[0].current.clone().sub(active[0].previous).normalize();
                const d1 = active[1].current.clone().sub(active[1].previous).normalize();
                // Calculate change in distance between the two fingers
                const prevDist = active[0].previous.clone().sub(active[1].previous).length();
                const currDist = active[0].current.clone().sub(active[1].current).length();
                const delta = prevDist - currDist;
                if (d1.dot(d0) < 0 && Math.abs(delta) > 1.5) {
                    for (const callback of callbacks) {
                        callback({
                            position: new Vector2()
                                .addVectors(active[0].current, active[1].current)
                                .multiplyScalar(0.5),
                            direction: new Vector2(delta, delta).normalize(),
                        });
                    }
                }
            }
        };
        this.canvas.addEventListener('wheel', wheelHandler);
        this.canvas.addEventListener('touchmove', touchHandler);
    }
    /**
     * Set the cursor icon.
     *
     * @param style Icon url or preset style
     */
    setCursor(style) {
        this.canvas.style.cursor = style;
    }
    /**
     * Set the input handlers to focus on the viewer's canvas element.
     */
    focus() {
        this.canvas.focus();
    }
}
